import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Grid, Divider, Chip, Avatar, List, Button, IconButton,
  ListItem, ListItemText, ListItemAvatar, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, MenuItem
} from '@mui/material';
import { AccessTime, Assignment, Warning, GppMaybe, Reply, ArrowForward, ChevronLeft, ChevronRight, AttachFile, BeachAccess } from '@mui/icons-material';
import { activitiesAPI, projectsAPI, blockagesAPI, holidaysAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DevBadge from '../../components/common/DevBadge';
import FileUpload from '../../components/common/FileUpload';
import SlideTransition from '../../components/common/SlideTransition';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/es';

dayjs.extend(isoWeek);
dayjs.locale('es');

function StatCard({ icon, label, value, color }) {
  return (
    <Card sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar sx={{ bgcolor: `${color}20`, color, width: 44, height: 44 }}>{icon}</Avatar>
      <Box>
        <Typography variant="h5" fontWeight={700}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
    </Card>
  );
}

export default function MemberDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [weekActivities, setWeekActivities] = useState([]);
  const [blockages, setBlockages] = useState([]);
  const [disputedActivities, setDisputedActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => dayjs().startOf('isoWeek'));
  const [replyDialog, setReplyDialog] = useState({ open: false, activity: null });
  const [descAppend, setDescAppend] = useState('');
  const [replyEvidences, setReplyEvidences] = useState([]);
  const [replySaving, setReplySaving] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [weekHolidays, setWeekHolidays] = useState([]);
  const [projectTotals, setProjectTotals] = useState({});
  const [currentWeekHours, setCurrentWeekHours] = useState(0);

  const weekEnd = weekStart.endOf('isoWeek');
  const isCurrentWeek = weekStart.isSame(dayjs().startOf('isoWeek'), 'day');

  const loadWeekActivities = useCallback(async (start, end) => {
    try {
      const res = await activitiesAPI.getAll({
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        limit: 200
      });
      const logs = res.data?.logs || res.data || [];
      setWeekActivities(logs);
      // Only update the stat-card counter when loading the actual current week
      if (start.isSame(dayjs().startOf('isoWeek'), 'day')) {
        setCurrentWeekHours(logs.reduce((sum, a) => sum + (a.hours || 0), 0));
      }
    } catch { }
  }, []);

  useEffect(() => {
    Promise.all([
      projectsAPI.getAll(),
      activitiesAPI.getAll({ limit: 5 }),
      blockagesAPI.getAll({ status: 'active' }),
      activitiesAPI.getAll({ disputeStatus: 'pending', limit: 10 }),
      activitiesAPI.getProjectTotals().catch(() => ({ data: [] }))
    ])
      .then(([prj, act, blk, disp, totals]) => {
        setProjects(prj.data);
        setRecentActivities(act.data?.logs || act.data || []);
        setBlockages(blk.data?.blockages || blk.data || []);
        setDisputedActivities(disp.data?.logs || disp.data || []);
        const totalsMap = {};
        (totals.data || []).forEach(t => { totalsMap[t._id?.toString()] = t; });
        setProjectTotals(totalsMap);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadWeekActivities(weekStart, weekEnd);
  }, [weekStart, loadWeekActivities]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load holidays for the active projects' clients whenever week or projects change
  useEffect(() => {
    const loadHolidays = async () => {
      const activeProjs = projects.filter(p => p.status === 'active' && p.clientId);
      if (activeProjs.length === 0) { setWeekHolidays([]); return; }
      const clientIds = [...new Set(activeProjs.map(p => p.clientId?.toString ? p.clientId.toString() : p.clientId))];
      try {
        const res = await holidaysAPI.getAll({
          clientIds: clientIds.join(','),
          startDate: weekStart.format('YYYY-MM-DD'),
          endDate: weekStart.endOf('isoWeek').format('YYYY-MM-DD')
        });
        setWeekHolidays(res.data || []);
      } catch { setWeekHolidays([]); }
    };
    loadHolidays();
  }, [weekStart, projects]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevWeek = () => setWeekStart(w => w.subtract(1, 'week'));
  const nextWeek = () => setWeekStart(w => w.add(1, 'week'));
  const goCurrentWeek = () => setWeekStart(dayjs().startOf('isoWeek'));

  const handleOpenReply = (act) => {
    setDescAppend('');
    setReplyEvidences(act.evidences || []);
    setReplyError('');
    setReplyDialog({ open: true, activity: act });
  };

  const handleCloseReply = () => setReplyDialog({ open: false, activity: null });

  const handleReplyFilesUploaded = (files) => {
    setReplyEvidences(prev => [...prev, ...files.map(f => ({ url: f.url, s3Key: f.s3Key, fileName: f.fileName }))]);
  };

  const handleReplySubmit = async () => {
    const act = replyDialog.activity;
    if (!act) return;
    setReplyError(''); setReplySaving(true);
    try {
      const finalDesc = descAppend.trim()
        ? `${act.description}\n\n[Adición]: ${descAppend.trim()}`
        : act.description;
      await activitiesAPI.update(act._id, {
        projectId: act.projectId,
        date: (act.date || '').slice(0, 10),
        startTime: act.startTime || '',
        endTime: act.endTime || '',
        hours: act.hours,
        description: finalDesc,
        category: act.category || '',
        relatedPeople: act.relatedPeople || [],
        evidences: replyEvidences
      });
      setDisputedActivities(prev => prev.filter(a => a._id !== act._id));
      handleCloseReply();
    } catch (err) {
      setReplyError(err.response?.data?.error || 'Error al guardar');
    } finally { setReplySaving(false); }
  };

  if (loading) return <LoadingSpinner />;

  const weekHours = weekActivities.reduce((sum, a) => sum + (a.hours || 0), 0);

  const weekExtraHours  = weekActivities.reduce((sum, a) => sum + (a.overtimeHours || 0), 0);
  const weekNormalHours = weekHours - weekExtraHours;

  return (
    <Box>
      <DevBadge id="page:member-dashboard" />
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Hola{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {dayjs().format('dddd, D [de] MMMM [de] YYYY')}
      </Typography>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Assignment />} label="Proyectos activos" value={projects.filter(p => p.status === 'active').length} color="#2979FF" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<AccessTime />} label="Horas esta semana" value={`${currentWeekHours.toFixed(1)}h`} color="#1a1a1a" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Warning />} label="Bloqueos activos" value={blockages.length} color="#D50000" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<GppMaybe />} label="Refutaciones pendientes" value={disputedActivities.length} color="#FF6D00" />
        </Grid>
      </Grid>

      {/* Weekly hours progress */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Progreso semanal
              {!isCurrentWeek && (
                <Chip label="semana anterior" size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.7rem', height: 18 }} />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {weekStart.format('D [de] MMMM')} — {weekEnd.format('D [de] MMMM [de] YYYY')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            {!isCurrentWeek && (
              <Button size="small" onClick={goCurrentWeek} sx={{ textTransform: 'none', fontSize: '0.75rem', mr: 0.5 }}>
                Hoy
              </Button>
            )}
            <IconButton size="small" onClick={prevWeek}><ChevronLeft fontSize="small" /></IconButton>
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 28, textAlign: 'center' }}>
              S{weekStart.isoWeek()}
            </Typography>
            <IconButton size="small" onClick={nextWeek} disabled={isCurrentWeek}><ChevronRight fontSize="small" /></IconButton>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            {weekHours.toFixed(1)}h registradas esta semana
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <span style={{ color: 'rgb(132, 0, 255)', fontWeight: 600 }}>{weekNormalHours.toFixed(1)}h normales</span>
            {weekExtraHours > 0 && (
              <>
                {' · '}
                <span style={{ color: '#ed6c02', fontWeight: 700 }}>+{weekExtraHours.toFixed(1)}h extra</span>
              </>
            )}
          </Typography>
        </Box>
        {/* Stacked bar: normal hours (purple) + overtime hours (orange) */}
        <Box sx={{ position: 'relative', height: 14, borderRadius: 7, bgcolor: '#E0E0E0', overflow: 'visible' }}>
          {weekHours > 0 && (
            <>
              {/* Normal hours segment */}
              <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${(weekNormalHours / weekHours) * 100}%`,
                bgcolor: 'rgb(132, 0, 255)',
                borderRadius: weekExtraHours > 0 ? '7px 0 0 7px' : '7px',
                transition: 'width 0.4s ease'
              }} />
              {/* Overtime segment */}
              {weekExtraHours > 0 && (
                <Box sx={{
                  position: 'absolute', left: `${(weekNormalHours / weekHours) * 100}%`, top: 0, bottom: 0,
                  width: `${(weekExtraHours / weekHours) * 100}%`,
                  bgcolor: '#ed6c02',
                  borderRadius: '0 7px 7px 0',
                  transition: 'width 0.4s ease'
                }} />
              )}
            </>
          )}
        </Box>
        {/* Holiday chips */}
        {weekHolidays.length > 0 && (
          <Box mt={1.5} display="flex" flexWrap="wrap" gap={0.5}>
            {weekHolidays.map(h => (
              <Chip
                key={h._id}
                size="small"
                icon={<BeachAccess sx={{ fontSize: '0.85rem !important' }} />}
                label={`${dayjs(h.date.slice(0, 10)).format('ddd D')} · ${h.name}`}
                color="info"
                variant="outlined"
                sx={{ fontSize: '0.72rem', height: 22 }}
              />
            ))}
          </Box>
        )}
      </Card>

      <Grid container spacing={3}>
        {/* Recent activities */}
        <Grid item xs={12} md={6}>
          <Card>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography variant="subtitle1" fontWeight={600}>Últimas Actividades</Typography>
            </Box>
            <List dense>
              {recentActivities.slice(0, 5).map((act, idx) => (
                <React.Fragment key={act._id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 34, height: 34, bgcolor: '#1a1a1a', fontSize: 12 }}>
                        {act.hours}h
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{act.description}</Typography>}
                      secondary={`${dayjs(act.date).format('DD/MM/YYYY')} · ${act.category || 'Sin categoría'}`}
                    />
                  </ListItem>
                  {idx < recentActivities.slice(0, 5).length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
              {recentActivities.length === 0 && (
                <ListItem><ListItemText secondary="No hay actividades registradas aún." /></ListItem>
              )}
            </List>
          </Card>
        </Grid>

        {/* My projects */}
        <Grid item xs={12} md={6}>
          <Card>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography variant="subtitle1" fontWeight={600}>Mis Proyectos</Typography>
            </Box>
            <List dense>
              {projects.map((p, idx) => {
                const t = projectTotals[p._id?.toString()] || {};
                const totalH  = (t.totalHours   || 0);
                const extraH  = (t.overtimeHours || 0);
                const normalH = Math.max(0, totalH - extraH);
                const fmtH = v => v % 1 === 0 ? `${v}h` : `${v.toFixed(1)}h`;
                return (
                  <React.Fragment key={p._id}>
                    <ListItem alignItems="flex-start" sx={{ py: 1.25 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                        }
                        secondary={
                          <Box display="flex" alignItems="center" gap={0.75} mt={0.5} flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              {p.clientId?.name || 'Sin cliente'}
                            </Typography>
                            <Chip
                              label={fmtH(normalH)}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700,
                                    bgcolor: 'rgba(132,0,255,0.10)', color: 'rgb(100,0,200)' }}
                            />
                            {extraH > 0 && (
                              <Chip
                                label={`+${fmtH(extraH)} extra`}
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700,
                                      bgcolor: 'rgba(237,108,2,0.12)', color: '#b45309' }}
                              />
                            )}
                          </Box>
                        }
                      />
                      <Chip
                        label={p.status === 'active' ? 'Activo' : 'Pausado'}
                        size="small"
                        color={p.status === 'active' ? 'success' : 'warning'}
                      />
                    </ListItem>
                    {idx < projects.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
              {projects.length === 0 && (
                <ListItem><ListItemText secondary="No estás asignado a proyectos todavía." /></ListItem>
              )}
            </List>
          </Card>
        </Grid>

        {/* Refuted activities */}
        <Grid item xs={12}>
          <Card>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <GppMaybe sx={{ color: 'warning.main', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={600}>Actividades Refutadas</Typography>
                {disputedActivities.length > 0 && (
                  <Chip label={disputedActivities.length} size="small" color="warning" />
                )}
              </Box>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/member/activities')}
                sx={{ textTransform: 'none' }}>
                Ver registro
              </Button>
            </Box>
            <List dense>
              {disputedActivities.slice(0, 3).map((act, idx) => (
                <React.Fragment key={act._id}>
                  <ListItem
                    secondaryAction={
                      <Button size="small" variant="outlined" color="warning"
                        onClick={() => handleOpenReply(act)}
                        startIcon={<Reply />}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                        Responder
                      </Button>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ width: 34, height: 34, bgcolor: 'rgba(255,152,0,0.15)', color: 'warning.main' }}>
                        <GppMaybe sx={{ fontSize: 18 }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{act.description}</Typography>}
                      secondary={
                        <>
                          {`${dayjs(act.date).format('DD/MM/YYYY')} · ${act.category || 'Sin categoría'} · ${act.hours}h`}
                          {act.dispute?.requestedBy && (
                            <Typography component="span" variant="caption" display="block" color="warning.dark" mt={0.25}>
                              Refutado por: {act.dispute.requestedBy.name || act.dispute.requestedBy.email}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  {idx < Math.min(disputedActivities.length, 3) - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
              {disputedActivities.length === 0 && (
                <ListItem>
                  <ListItemText secondary="Sin refutaciones pendientes. ¡Todas tus actividades están al día!" />
                </ListItem>
              )}
            </List>
          </Card>
        </Grid>
      </Grid>

      {/* Dispute reply dialog — same structure as modal:actividad in dispute mode */}
      <Dialog open={replyDialog.open} onClose={handleCloseReply} maxWidth="md" fullWidth TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Reply color="warning" />
          Responder Refutación
          <DevBadge id="modal:actividad" inline />
        </DialogTitle>
        <DialogContent>
          {replyDialog.activity && (
            <Alert severity="warning" icon={<GppMaybe />} sx={{ mb: 2 }}>
              <strong>Actividad refutada por tu líder:</strong>{' '}
              {replyDialog.activity.dispute?.requestedBy && (
                <span>
                  <strong>{replyDialog.activity.dispute.requestedBy.name || replyDialog.activity.dispute.requestedBy.email}</strong>
                  {replyDialog.activity.dispute.requestedAt && ` · ${dayjs(replyDialog.activity.dispute.requestedAt).format('DD/MM/YYYY HH:mm')}`}
                  {' — '}
                </span>
              )}
              {replyDialog.activity.dispute?.message}
              <Typography variant="caption" display="block" mt={0.5}>
                Complementa con más detalle o evidencias y guarda para marcarla como resuelta.
              </Typography>
            </Alert>
          )}
          {replyError && <Alert severity="error" sx={{ mb: 2 }}>{replyError}</Alert>}
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Proyecto" value={
                  projects.find(p => p._id === replyDialog.activity?.projectId)?.name ||
                  replyDialog.activity?.projectName || '—'
                }
                fullWidth disabled
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Fecha"
                value={replyDialog.activity ? dayjs((replyDialog.activity.date || '').slice(0, 10)).format('DD/MM/YYYY') : ''}
                fullWidth disabled
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Horas registradas"
                value={replyDialog.activity ? `${replyDialog.activity.hours}h` : ''}
                fullWidth disabled
                sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.65)' } }}
              />
            </Grid>
            {replyDialog.activity?.startTime && replyDialog.activity?.endTime && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Horario"
                  value={`${replyDialog.activity.startTime} – ${replyDialog.activity.endTime}`}
                  fullWidth disabled
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.65)' } }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Descripción original (solo lectura)"
                value={replyDialog.activity?.description || ''}
                multiline rows={2} fullWidth disabled
                sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.65)' } }}
              />
              <TextField
                label="Información adicional"
                value={descAppend}
                onChange={e => setDescAppend(e.target.value)}
                multiline rows={2} fullWidth autoFocus
                placeholder="Añade detalles o complementa la descripción original..."
                sx={{ mt: 1.5 }}
                helperText="Solo puedes añadir información. El texto original no puede modificarse ni eliminarse."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Categoría"
                value={replyDialog.activity?.category || 'Sin categoría'}
                fullWidth disabled
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" mb={1}>
                Evidencias (solo lectura — solo puedes añadir nuevos archivos)
              </Typography>
              {replyDialog.activity?.evidences?.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                  {replyDialog.activity.evidences.filter(Boolean).map((ev, i) => {
                    const name = typeof ev === 'string'
                      ? ev.split('/').pop()
                      : (ev.fileName || ev.s3Key?.split('/').pop() || `Archivo ${i + 1}`);
                    return (
                      <Chip key={i} icon={<AttachFile />} label={name?.substring(0, 35)}
                        size="small" variant="outlined" />
                    );
                  })}
                </Box>
              )}
              <FileUpload onFilesUploaded={handleReplyFilesUploaded} maxFiles={10} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseReply}>Cancelar</Button>
          <Button variant="contained" onClick={handleReplySubmit}
            disabled={replySaving}>
            {replySaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
